import { formatDate } from '@/shared/lib/dateUtils'
import { Badge } from '@/shared/components/Badge'
import { colorDeEstatusDeCandidato } from '@/features/rh/lib/colorDeEstatusDeCandidato'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { RH } from '@/constants/permissions'
import { Checkbox } from '@/shared/components/Checkbox'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { apiService } from '@/services/api'
import type { Candidate, ApprovedJobRequest } from '@/services/api'
import { useAuth } from '@/shared/hooks/useAuth'
import { userHasPermission } from '@/shared/lib/roleUtils'
import { Notification, useNotification } from '@/shared/components/Notification'
import { hrInterviewService } from '@/features/rh/api/hrInterviewService'
import type { HRInterviewRegistration } from '@/types/hrInterview'
import { Recorrido } from '@/shared/components/Recorrido'
import {
  crearPasosDescartados,
  guardarRecorridoVisto,
  yaVioElRecorrido,
  DESCARTADOS_DE_EJEMPLO,
} from '@/features/rh/lib/recorridoDescartados'
import { 
  Users, 
  Calendar, 
  XCircle,
  RefreshCw,
  MoreVertical,
  Loader2,
  Search,
  Trash2,
  Save,
  User,
  ArrowLeft,
  X,
  CircleQuestionMark,
  UserPlus,
  CalendarPlus,
} from 'lucide-react'
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage'
import { JobRequestsHistory } from '@/pages/rh/JobRequestsHistory'
import { useFilaSaliente } from '@/shared/hooks/useFilaSaliente'
import {
  EsqueletoLinea,
  EsqueletoTabla,
  EsqueletoTarjetasTexto,
} from '@/shared/components/Esqueleto'
import { useAccionesDelCandidato } from '@/features/rh/hooks/useAccionesDelCandidato'
import {
  COLUMNAS,
  accionesDisponibles,
  etiquetaDeConteo,
  coincideBusqueda,
  pastillaDeTarjeta,
  etiquetaDeEtapa,
  nombreDeQuienDescarto,
  fechaHoraDeDescarte,
  fechaDeDescarte,
  etiquetaDeVacante,
  type ClaveColumna,
  type Pestana,
  pideEntrevistaDelSolicitante,
} from '@/features/rh/lib/reclutamientoTablero'
import {
  nombreDeLaEtapaDeRegreso,
  vacantesParaRegresar,
} from '@/features/rh/lib/regresoDeCandidato'
import {
  ETIQUETA_EVALUACION_PENDIENTE,
  tieneEvaluacionPendiente,
} from '@/features/rh/lib/evaluacionPendiente'
import {
  ETIQUETA_VACANTE_CUBIERTA,
  idsDeVacantes,
  vacanteCubiertaParaElCandidato,
} from '@/features/rh/lib/coberturaDeVacante'

interface CandidateColumnProps {
  title: string
  candidates: Candidate[]
  onCandidateClick: (candidateId: string) => void
  onActionClick: (candidateId: string, action: string) => void
  count: number
  /** Color de identidad de la columna, en hexadecimal. */
  color: string
  processingCandidateId: string | null
  /** Acción que produce el estado de esta columna, para el arrastre. */
  accionDeEntrada?: string
  /** Candidato que se está arrastrando ahora mismo, si hay alguno. */
  arrastrando: Candidate | null
  onArrastrarInicio: (candidate: Candidate) => void
  onArrastrarFin: () => void
  onSoltar: (candidate: Candidate, accion: string) => void
  /** Decide si esta columna acepta al candidato que viene arrastrado. */
  aceptaSoltar: (candidate: Candidate, accion?: string) => boolean
}

/** Tipografía de los datos de la tarjeta, según el diseño. */
const ETIQUETA = { fontWeight: 500, fontSize: '12px', lineHeight: '100%' } as const
const VALOR = { fontWeight: 400, fontSize: '13px', lineHeight: '100%' } as const

// Componente de columna para candidatos
const CandidateColumn = ({
  title,
  candidates,
  onCandidateClick,
  onActionClick,
  count,
  color,
  processingCandidateId,
  accionDeEntrada,
  arrastrando,
  onArrastrarInicio,
  onArrastrarFin,
  onSoltar,
  aceptaSoltar,
}: CandidateColumnProps) => {
  const { user } = useAuth();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null)
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  const isProcessing = (candidateId: string) => processingCandidateId === candidateId

  const getAvailableActions = (candidate: Candidate) =>
    accionesDisponibles(
      candidate.status,
      user?.rbac?.effectivePermissions || [],
    )

  // Solo se ilumina la columna si de verdad puede recibir a quien se arrastra:
  // el arrastre usa las mismas reglas de permisos que el menú de acciones.
  const puedeRecibir = !!arrastrando && aceptaSoltar(arrastrando, accionDeEntrada)

  return (
    // El cuerpo va transparente para que se vea el fondo de la página; el
    // color de la columna vive solo en el contenedor del título.
    <div
      className={`rounded-xl flex flex-col h-full border transition-colors ${
        puedeRecibir ? 'border-dashed' : ''
      }`}
      style={{ borderColor: puedeRecibir ? '#315FB9' : '#E0E0E0' }}
      onDragOver={(e) => {
        if (!puedeRecibir) return
        // Sin preventDefault el navegador no permite soltar aquí.
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
      }}
      onDrop={(e) => {
        if (!puedeRecibir || !accionDeEntrada) return
        e.preventDefault()
        onSoltar(arrastrando!, accionDeEntrada)
      }}
    >
      {/* Encabezado delgado, al estilo de un tablero de Jira: el título es
          una etiqueta de la columna, no un titular. Alto el que pida el
          contenido, para que el espacio se lo lleven las tarjetas. */}
      <div
        className="px-3 py-2 rounded-t-xl border-b"
        style={{ backgroundColor: color, borderColor: '#E0E0E0' }}
      >
        <div className="flex items-center justify-between gap-2">
          <h3
            className="text-gray-600 uppercase truncate"
            style={{
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '20px',
              letterSpacing: '0.04em',
            }}
          >
            {title}
          </h3>
          <Badge color="morado" className="flex-shrink-0 tabular-nums">
            {etiquetaDeConteo(count)}
          </Badge>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pt-3 pb-2 space-y-2">
        {candidates.map((candidate) => {
          const availableActions = getAvailableActions(candidate)
          const isMenuOpen = openMenuId === candidate.id
          // Los estatus del cierre comparten columna con pre_seleccionado pero
          // dicen otra cosa, así que traen su propia pastilla (RH-03.1).
          const pastillaPropia = pastillaDeTarjeta(candidate.status)
          
          return (
            <div
              key={candidate.id}
              draggable={!isProcessing(candidate.id) && availableActions.length > 0}
              onDragStart={(e) => {
                // Firefox no arranca el arrastre si no hay datos puestos.
                e.dataTransfer.setData('text/plain', candidate.id)
                e.dataTransfer.effectAllowed = 'move'
                onArrastrarInicio(candidate)
              }}
              onDragEnd={onArrastrarFin}
              className={`bg-white rounded-lg p-3 transition-all cursor-grab active:cursor-grabbing relative ${
                isProcessing(candidate.id) ? 'opacity-60 pointer-events-none' : ''
              } ${arrastrando?.id === candidate.id ? 'opacity-40' : ''}`}
              style={{ boxShadow: '0px 1px 3px 1px #00000026' }}
              onClick={() => onCandidateClick(candidate.id)}
            >
              {/* Overlay de carga */}
              {isProcessing(candidate.id) && (
                <div className="absolute inset-0 bg-white bg-opacity-50 rounded-lg flex items-center justify-center z-20">
                  <div className="bg-blue-600 text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm font-medium">Procesando...</span>
                  </div>
                </div>
              )}
              
              <div className="flex items-start gap-2 mb-1">
                <h4
                  className="text-gray-900 flex-1 min-w-0 break-words"
                  style={{ fontWeight: 600, fontSize: '14px', lineHeight: '100%' }}
                >
                  {candidate.fullName}
                </h4>
                {availableActions.length > 0 && (
                  <button
                    className="p-0.5 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                    style={{ color: '#477FFA' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setOpenMenuId(isMenuOpen ? null : candidate.id)
                    }}
                    title="Acciones rápidas"
                    disabled={isProcessing(candidate.id)}
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Menú de acciones rápidas */}
              {isMenuOpen && availableActions.length > 0 && (
                <div className="absolute right-2 top-9 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[160px]">
                  {availableActions.map((action) => (
                    <button
                      key={action.id}
                      onClick={(e) => {
                        e.stopPropagation()
                        onActionClick(candidate.id, action.id)
                        setOpenMenuId(null)
                      }}
                      disabled={isProcessing(candidate.id)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2.5 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                      style={{ color: '#59595C' }}
                    >
                      {/* El rojo es solo del icono de descartar; el texto va
                          en el mismo gris que el resto del menú. */}
                      <span style={action.id === 'reject' ? { color: '#EF4444' } : undefined}>
                        {isProcessing(candidate.id) ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          action.icon
                        )}
                      </span>
                      <span>{action.label}</span>
                    </button>
                  ))}
                </div>
              )}
            
              <div className="space-y-.5">
                <p className="text-gray-800">
                  <span style={ETIQUETA}>Vacante: </span>
                  <span style={VALOR}>{candidate.jobRequest?.puestoSolicitar || 'No especificado'}</span>
                </p>
                <p className="text-gray-800">
                  <span style={ETIQUETA}>Registro: </span>
                  <span style={VALOR}>{formatDate(candidate.createdAt)}</span>
                </p>
              </div>

              {/* Solo la pastilla que dice algo que la columna no dice.
                  La de estado repetia el titulo de la columna en la que ya
                  esta la tarjeta, asi que no aportaba nada; estas otras
                  distinguen entre tarjetas de una misma columna. */}
              {pastillaPropia && (
              <Badge
                color={colorDeEstatusDeCandidato(candidate.status, 'azul')}
                className="mt-2.5"
              >
                {pastillaPropia.etiqueta}
              </Badge>
              )}

              {/* La vacante pide entrevista del solicitante, así que aquí no
                  hay «Aprobar»: la decisión se registra al cargar el resultado
                  de esa entrevista. Sin decirlo, la tarjeta solo se veía sin
                  acciones y no se entendía por qué. */}
              {pideEntrevistaDelSolicitante(candidate) && (
                <Badge
                  color="azul"
                  icon={<CalendarPlus className="w-3 h-3 flex-shrink-0" />}
                  className="mt-2"
                  title="La decisión se toma al registrar el resultado de la entrevista del solicitante"
                >
                  Requiere entrevista del solicitante
                </Badge>
              )}

              {/* La vacante se cubrió y este candidato sigue en proceso (SRH-313):
                  RH decide qué hacer con él. */}
              {vacanteCubiertaParaElCandidato(candidate) && (
                <Badge
                  color="naranja"
                  className="mt-2"
                  title="Las plazas de esta vacante ya se cubrieron. RH decide si lo descarta o lo selecciona de todos modos"
                >
                  {ETIQUETA_VACANTE_CUBIERTA}
                </Badge>
              )}

              {/* Alta manual en Seleccionado sin su evaluación (SRH-309): está
                  marcado y sin efectos hasta que se suba. */}
              {tieneEvaluacionPendiente(candidate) && (
                <Badge
                  color="amarillo"
                  className="mt-2"
                  title="No se le avisa que fue seleccionado ni se avisa a RH para contratarlo hasta que se suba su evaluación"
                >
                  {ETIQUETA_EVALUACION_PENDIENTE}
                </Badge>
              )}
            </div>
          )
        })}

        {/* Silueta de destino, siempre al final de la columna. Dice dónde se
            puede soltar sin obligar a empezar a arrastrar para descubrirlo, y
            de paso le da fondo a la columna vacía. Cuando la columna acepta al
            candidato que viene en el aire toma el mismo azul que su borde. */}
        <div
          className={`mt-1 rounded-xl border-2 border-dashed p-3 transition-colors ${
            puedeRecibir
              ? 'border-[#315FB9] bg-[#315FB9]/5'
              : 'border-[#D1D5DB] bg-white/70'
          }`}
        >
          <div className="mb-2.5 flex items-center gap-2">
            <div className="h-5 w-20 rounded-full bg-[#E5E7EB]" />
            <div className="h-5 w-14 rounded-full bg-[#E5E7EB]" />
          </div>
          <div className="mb-1.5 h-4 w-3/4 rounded bg-[#F3F4F6]" />
          <div className="h-3 w-1/2 rounded bg-[#F3F4F6]" />
          <p className="mt-3 text-center text-xs font-medium text-[#9CA3AF]">
            Arrastra aquí
          </p>
        </div>
      </div>
    </div>
  )
}

export const RHDashboard = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isLoading: authLoading } = useAuth()
  const { notification, showSuccess, showError, hideNotification } = useNotification()
  
  // Vacantes y Candidatos - Vista Kanban principal
  const [unReviewedCandidates, setUnReviewedCandidates] = useState<Candidate[]>([])
  const [preselectedCandidates, setPreselectedCandidates] = useState<Candidate[]>([])
  const [hrInterviewCandidates, setHrInterviewCandidates] = useState<Candidate[]>([])
  const [sentToRequesterCandidates, setSentToRequesterCandidates] = useState<Candidate[]>([])
  const [approvedByRequesterCandidates, setApprovedByRequesterCandidates] = useState<Candidate[]>([])
  const [rejectedCandidates, setRejectedCandidates] = useState<Candidate[]>([])
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [pestana, setPestana] = useState<Pestana>('activos')
  const [arrastrando, setArrastrando] = useState<Candidate | null>(null)
  const [busqueda, setBusqueda] = useState('')
  const [pagina, setPagina] = useState(1)
  const [seleccionados, setSeleccionados] = useState<string[]>([])
  const { hasPermission } = usePermissions()
  // El permiso no viene en ningun rol: se otorga uno por uno.
  const puedeDarDeAlta = hasPermission(RH.RECLUTAMIENTO_ALTA_MANUAL)

  /**
   * Aviso de alta: lo da esta pantalla porque la del alta ya se fue cuando el
   * registro termina. Se limpia el estado de la navegación para que no vuelva
   * a salir al recargar o al regresar con el botón del navegador.
   */
  useEffect(() => {
    const estado = location.state as { candidatoRegistrado?: boolean } | null
    if (!estado?.candidatoRegistrado) return
    showSuccess('Candidato registrado', 'Ya aparece en la etapa que elegiste.')
    navigate(location.pathname, { replace: true, state: null })
  }, [location.state, location.pathname, navigate, showSuccess])
  const [porEliminar, setPorEliminar] = useState<string[] | null>(null)
  const [recorridoAbierto, setRecorridoAbierto] = useState(false)
  /** Candidato cuyo comentario de descarte se está viendo. */
  const [verComentario, setVerComentario] = useState<Candidate | null>(null)
  /** Candidato que se está devolviendo al tablero, y los datos del regreso. */
  const [porDevolver, setPorDevolver] = useState<Candidate | null>(null)
  const [vacanteDestino, setVacanteDestino] = useState('')
  /** A que etapa volvería en la vacante elegida: la decide su historial (SRH-320). */
  const [etapaDeRegreso, setEtapaDeRegreso] = useState<{ stage: string; reason: string } | null>(null)
  const [consultandoEtapa, setConsultandoEtapa] = useState(false)
  /** La ultima vacante cuya etapa se pidio: descarta respuestas que llegan tarde. */
  const vacanteConsultada = useRef('')
  const [errorDeEtapa, setErrorDeEtapa] = useState<string | null>(null)
  const [motivoReactivacion, setMotivoReactivacion] = useState('')
  const [devolviendo, setDevolviendo] = useState(false)
  /** Vacantes activas, para elegir a cuál regresa el candidato. */
  const [vacantes, setVacantes] = useState<ApprovedJobRequest[]>([])
  const [eliminando, setEliminando] = useState(false)
  const { claseDeFila, animarSalida, cancelarSalida } = useFilaSaliente()

  /**
   * Cambiar de pestaña limpia el filtro: el término que servía para el tablero
   * casi nunca sirve para los descartados, y arrastrarlo daría la impresión de
   * que la otra pestaña está vacía.
   */
  const cambiarPestana = (destino: Pestana) => {
    setPestana(destino)
    setBusqueda('')
    setPagina(1)
    setSeleccionados([])
  }

  // Estados para el modal de entrevista
  const [showInterviewModal, setShowInterviewModal] = useState(false)
  const [, setSelectedCandidateForInterview] = useState<string | null>(null)
  const [availableInterviewers, setAvailableInterviewers] = useState<any[]>([])
  const [isSubmittingInterview, setIsSubmittingInterview] = useState(false)
  const [interviewForm, setInterviewForm] = useState<HRInterviewRegistration>({
    candidateId: '',
    fechaEntrevista: '',
    horaEntrevista: '',
    modalidad: 'presencial',
    ubicacionLink: '',
    entrevistadorId: '',
    observaciones: ''
  })
  const [interviewErrors, setInterviewErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!authLoading) {
      loadAllData()
    }
  }, [authLoading])

  const loadAllData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      await loadVacantesYCandidatos()
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || 'Error cargando datos')
    } finally {
      setLoading(false)
    }
  }

  const loadVacantesYCandidatos = async () => {
    try {
      // Cargar vacantes activas
      const jobs = await apiService.getApprovedJobRequests()
      // Las mismas vacantes alimentan el select de devolver al tablero: ya
      // están pedidas, no hace falta otra llamada.
      setVacantes(jobs)
      
      // Una sola llamada con todas las vacantes. Antes se pedia una por una,
      // porque el back solo tenia el endpoint por solicitud, y con suficientes
      // vacantes abiertas el servidor cortaba con un 429.
      // Una vacante cubierta ya no esta entre las activas, pero sus candidatos en
      // proceso no se descartan solos: RH decide (SRH-313). Sin sumarlas aqui
      // desaparecerian del tablero junto con la vacante. Si esa consulta falla no
      // se tumba el tablero: se muestra sin ellos.
      const cubiertas = await apiService
        .getCoveredJobRequestsWithOpenCandidates()
        .catch(() => [])
      const allCandidates = await apiService.getCandidatesByJobRequests(
        idsDeVacantes(jobs, cubiertas),
      )
      
      // Filtrar candidatos por estado según el nuevo diseño
      setUnReviewedCandidates(allCandidates.filter((c: Candidate) => 
        c.status === 'pre_registro' || c.status === 'registrado' || c.status === 'pendiente'
      ))
      setPreselectedCandidates(allCandidates.filter((c: Candidate) => 
        c.status === 'en_proceso'
      ))
      setHrInterviewCandidates(allCandidates.filter((c: Candidate) => c.status === 'entrevista_rh'))
      setSentToRequesterCandidates(allCandidates.filter((c: Candidate) =>
        c.status === 'pre_seleccionado' ||
        c.status === 'entrevista_solicitante'
      ))
      setApprovedByRequesterCandidates(allCandidates.filter((c: Candidate) =>
        c.status === 'seleccionado' ||
        // RH-03.1: el solicitante ya los eligió. Siguen aquí mientras RH o
        // Dirección cierran, con la pastilla de en qué momento van.
        c.status === 'pendiente_cierre' ||
        c.status === 'pendiente_direccion' ||
        c.status === 'decision_direccion'
      ))
      setRejectedCandidates(allCandidates.filter((c: Candidate) => 
        c.status === 'rechazado' || c.status === 'rechazado_evaluacion_rh'
      ))
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error loading vacantes y candidatos:', err)
      throw err
    }
  }

  const handleCandidateClick = (candidateId: string) => {
    navigate(`/candidate/${candidateId}`)
  }

  const loadAvailableInterviewers = async () => {
    try {
      const usersData = await apiService.getUsers();
      const eligibleUsers = (usersData ?? []).filter((u: any) =>
        userHasPermission("rh.entrevistas.base.create", u?.rbac?.effectivePermissions),
      );
      const normalizedInterviewers = eligibleUsers.map((u: any) => ({
        ...u,
        role: u?.roles?.[0]?.name ?? u?.rbac?.roles?.[0]?.name ?? '',
      }))
      setAvailableInterviewers(normalizedInterviewers)
    } catch (err) {
      console.error('Error al cargar entrevistadores:', err)
    }
  }

  const handleInterviewFormChange = (field: keyof HRInterviewRegistration, value: string) => {
    setInterviewForm(prev => ({
      ...prev,
      [field]: value
    }))
    
    if (interviewErrors[field]) {
      setInterviewErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const handleSubmitInterview = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmittingInterview(true)
    setInterviewErrors({})
    
    try {
      const validation = hrInterviewService.validateInterviewRegistration(interviewForm)
      
      if (!validation.isValid && validation.errors) {
        const errors: Record<string, string> = {}
        Object.entries(validation.errors).forEach(([key, value]) => {
          errors[key] = value
        })
        setInterviewErrors(errors)
        setIsSubmittingInterview(false)
        return
      }
      
      await hrInterviewService.registerHRInterview(interviewForm)
      
      setShowInterviewModal(false)
      setSelectedCandidateForInterview(null)
      
      // Recargar datos
      await loadVacantesYCandidatos()
      
      showSuccess('¡Entrevista programada!', 'La entrevista RH ha sido programada exitosamente.')
      
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al programar entrevista:', err)
      showError('Error', err.message || 'Error al programar la entrevista')
    } finally {
      setIsSubmittingInterview(false)
    }
  }

  /**
   * Candidatos por columna. Un solo lugar que decide qué estado va en qué
   * columna, para que el arrastre y el conteo no puedan discrepar.
   */
  const candidatosDeColumna = (clave: ClaveColumna): Candidate[] =>
    columnaSinFiltrar(clave).filter((c) => coincideBusqueda(c, busqueda))

  const columnaSinFiltrar = (clave: ClaveColumna): Candidate[] => {
    switch (clave) {
      case 'sin_revision': return unReviewedCandidates
      case 'en_proceso': return preselectedCandidates
      case 'entrevista_rh': return hrInterviewCandidates
      case 'pre_seleccionado': return sentToRequesterCandidates
      case 'seleccionado': return approvedByRequesterCandidates
    }
  }

  /**
   * Si el candidato arrastrado puede terminar en esa columna.
   *
   * Se consulta la misma lista de acciones que pinta el menú de la tarjeta: si
   * la acción de entrada de la columna no está entre las suyas, la columna no se
   * ilumina y soltar no hace nada.
   */
  const aceptaSoltar = (candidate: Candidate, accion?: string): boolean => {
    if (!accion) return false
    const acciones = accionesDisponibles(
      candidate.status,
      user?.rbac?.effectivePermissions || [],
    )
    return acciones.some((a) => a.id === accion)
  }

  // Durante el recorrido la bandeja se rellena hasta cinco con filas de
  // ejemplo: la primera vez suele estar vacia, y ahi no se ve donde queda el
  // boton de devolver ni para que sirven las casillas. Salen del arreglo en
  // memoria y desaparecen al cerrar; no se guardan ni se piden al API.
  const descartadosVisibles = recorridoAbierto
    ? [
        ...rejectedCandidates,
        ...DESCARTADOS_DE_EJEMPLO.slice(0, Math.max(0, 5 - rejectedCandidates.length)),
      ]
    : rejectedCandidates

  const descartadosFiltrados = descartadosVisibles.filter((c) =>
    coincideBusqueda(c, busqueda),
  )
  const POR_PAGINA = 10
  const totalPaginas = Math.max(1, Math.ceil(descartadosFiltrados.length / POR_PAGINA))
  const descartadosPagina = descartadosFiltrados.slice(
    (pagina - 1) * POR_PAGINA,
    pagina * POR_PAGINA,
  )
  // La casilla del encabezado marca lo de la página que se ve, no los 23 de
  // todas las páginas: seleccionar lo que no está a la vista sorprende.
  const todosSeleccionados =
    descartadosPagina.length > 0 &&
    descartadosPagina.every((c) => seleccionados.includes(c.id))

  // Los conteos de las pestañas son totales, no del filtro: dicen cuánto hay,
  // no cuánto coincide. Los contadores de cada columna sí siguen al filtro.
  const totalActivos = COLUMNAS.reduce(
    (suma, columna) => suma + columnaSinFiltrar(columna.clave).length,
    0,
  )

  /**
   * Un solo camino para el bote de basura de una fila y para vaciar la
   * selección: el servidor recibe siempre una lista de ids.
   */
  /**
   * Abre el modal de devolver al tablero. La vacante arranca vacía a propósito
   * —hay que elegirla, porque la original pudo cubrirse— y la columna también.
   */
  // El recorrido sale solo la primera vez que este usuario entra a la pantalla.
  // Se espera a tener su id: la marca es por usuario, no por navegador.
  useEffect(() => {
    if (!user?.id) return
    if (!yaVioElRecorrido(user.id)) setRecorridoAbierto(true)
  }, [user?.id])

  const pasosRecorrido = useMemo(() => crearPasosDescartados({
    irADescartados: () => setPestana('descartados'),
    // El boton de eliminar solo existe con seleccion, asi que su paso marca al
    // primero de la pagina para tener que senalar.
    marcarPrimero: () =>
      setSeleccionados((previos) =>
        previos.length > 0 || descartadosPagina.length === 0
          ? previos
          : [descartadosPagina[0].id],
      ),
  }), [descartadosPagina])

  const cerrarRecorrido = () => {
    setRecorridoAbierto(false)
    if (user?.id) guardarRecorridoVisto(user.id)
    // La seleccion la puso el recorrido, no el usuario: se deshace al salir
    // para no dejarlo con el boton de eliminar encendido.
    setSeleccionados([])
  }

  const abrirDevolver = (candidato: Candidate) => {
    setPorDevolver(candidato)
    setVacanteDestino('')
    setEtapaDeRegreso(null)
    setErrorDeEtapa(null)
    setMotivoReactivacion('')
  }

  /**
   * Al elegir la vacante se le pregunta al servidor a que etapa volveria: no
   * se elige, la decide su historial. De paso valida que la vacante sirva, y
   * si no, lo dice aqui y no hasta confirmar.
   */
  const elegirVacanteDeRegreso = async (valor: string) => {
    setVacanteDestino(valor)
    setEtapaDeRegreso(null)
    setErrorDeEtapa(null)
    vacanteConsultada.current = valor
    if (!porDevolver || !valor) {
      setConsultandoEtapa(false)
      return
    }

    setConsultandoEtapa(true)
    try {
      const respuesta = await apiService.getReturnStage(porDevolver.id, Number(valor))
      // Si cambio de vacante mientras llegaba la respuesta, esta ya no aplica.
      if (vacanteConsultada.current === valor) setEtapaDeRegreso(respuesta)
    } catch (errCrudo: unknown) {
      const fallo = errCrudo as { message?: string }
      if (vacanteConsultada.current === valor) {
        setErrorDeEtapa(fallo?.message || 'No se pudo saber a qué etapa volvería')
      }
    } finally {
      if (vacanteConsultada.current === valor) setConsultandoEtapa(false)
    }
  }

  const confirmarDevolucion = async () => {
    if (!porDevolver || !vacanteDestino || !etapaDeRegreso) return
    setDevolviendo(true)
    try {
      await apiService.reactivateCandidate(porDevolver.id, {
        jobRequestId: Number(vacanteDestino),
        motivo: motivoReactivacion,
      })
      // Ya no está descartado: si estaba seleccionado en la tabla, se saca de la
      // selección para que vaciar descartados no lo cuente.
      setSeleccionados((previos) => previos.filter((id) => id !== porDevolver.id))
      setPorDevolver(null)
      showSuccess(
        'Candidato devuelto al tablero',
        etapaDeRegreso
          ? `Volvió a ${nombreDeLaEtapaDeRegreso(etapaDeRegreso.stage)}, con su historial.`
          : 'Ya aparece en el tablero, con su historial.',
      )
      await loadAllData()
    } catch (error: unknown) {
      const fallo = error as {
        response?: { data?: { message?: string | string[] } }
        message?: string
      }
      const detalle = fallo?.response?.data?.message ?? fallo?.message
      showError(
        'No se pudo devolver',
        Array.isArray(detalle)
          ? detalle.join(' ')
          : detalle || 'No se pudo devolver al candidato al tablero',
      )
    } finally {
      setDevolviendo(false)
    }
  }

  const eliminarDescartados = async (ids: string[]) => {
    setEliminando(true)
    try {
      await apiService.request('/candidates/discarded/delete', {
        method: 'POST',
        body: JSON.stringify({ ids }),
      })
      // A-16: las filas salen y hasta entonces se recarga el tablero.
      await animarSalida(ids)
      setSeleccionados((previos) => previos.filter((id) => !ids.includes(id)))
      setPorEliminar(null)
      showSuccess(
        ids.length === 1 ? 'Candidato eliminado' : 'Candidatos eliminados',
        ids.length === 1
          ? 'El candidato descartado se eliminó.'
          : `Se eliminaron ${ids.length} candidatos descartados.`,
      )
      await loadAllData()
    } catch (error: unknown) {
      cancelarSalida(ids)
      const fallo = error as {
        response?: { data?: { message?: string | string[] } }
        message?: string
      }
      const detalle = fallo?.response?.data?.message ?? fallo?.message
      showError(
        'No se pudo eliminar',
        Array.isArray(detalle)
          ? detalle.join(' ')
          : detalle || 'No se pudieron eliminar los candidatos',
      )
    } finally {
      setEliminando(false)
    }
  }

  const handleSoltar = (candidate: Candidate, accion: string) => {
    setArrastrando(null)
    handleActionClick(candidate.id, accion)
  }

  // SRH-339: lo que pasa al elegir una acción lo comparten el tablero y la ficha.
  // Aquí solo queda lo propio del tablero: recargar, avisar y el modal de
  // entrevista de RH.
  const { ejecutar: ejecutarAccion, procesandoId, modales: modalesDeAcciones } =
    useAccionesDelCandidato({
      alActualizar: () => loadVacantesYCandidatos(),
      avisar: { exito: showSuccess, error: showError },
      agendarEntrevistaDeRH: async (candidato) => {
        setSelectedCandidateForInterview(candidato.id)
        setInterviewForm({
          candidateId: candidato.id,
          fechaEntrevista: '',
          horaEntrevista: '',
          modalidad: 'presencial',
          ubicacionLink: '',
          entrevistadorId: '',
          observaciones: ''
        })
        setInterviewErrors({})
        // Cargar entrevistadores disponibles
        await loadAvailableInterviewers()
        setShowInterviewModal(true)
      },
    })

  const handleActionClick = async (candidateId: string, action: string) => {
    const candidato = [
      ...unReviewedCandidates,
      ...preselectedCandidates,
      ...hrInterviewCandidates,
      ...sentToRequesterCandidates,
      ...approvedByRequesterCandidates,
    ].find((c) => c.id === candidateId)
    if (candidato) await ejecutarAccion(candidato, action)
  }

  // Mostrar loading mientras se verifica la autenticación
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando dashboard...</p>
        </div>
      </div>
    )
  }

  // Verificar permisos después de cargar autenticación
  if (
    userHasPermission(
      "rh.reclutamiento_candidatos.base.read",
      user?.rbac?.effectivePermissions || []
    ) === false &&
    userHasPermission(
      "rh.solicitudes_personal.base.create",
      user?.rbac?.effectivePermissions || []
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  // A-01: la silueta depende del tab. Activos es un tablero de columnas con
  // tarjetas; descartados e historial son tablas, y una silueta que no se
  // parece al contenido produce justo el salto que la animacion evita.
  if (loading) {
    return (
      <div className="p-6">
        {pestana === 'activos' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, columna) => (
              <div key={columna} className="space-y-3">
                <EsqueletoLinea className="w-2/3 h-4" />
                <EsqueletoTarjetasTexto cuantas={2} columnas="" campos={2} />
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <EsqueletoTabla filas={8} columnas={6} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 mt-8 pb-12">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-3">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <TituloDePantalla>Panel de candidatos</TituloDePantalla>
              {/* Mismo tooltip negro que se usa en el alta de contratación. */}
              <div className="relative group flex-shrink-0">
                <button
                  onClick={() => setRecorridoAbierto(true)}
                  aria-label="Ver recorrido de la bandeja de descartados"
                  className="p-1 rounded-full text-gray-400 hover:text-[#253579] hover:bg-gray-100 transition-colors"
                >
                  <CircleQuestionMark className="w-5 h-5" />
                </button>
                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 hidden group-hover:block whitespace-nowrap bg-black text-white text-xs py-1 px-2 rounded shadow-lg z-50">
                  Ver recorrido
                </span>
              </div>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Posiciones aprobadas donde los candidatos pueden registrarse</p>
          </div>
        </div>

        {/* Pestañas con el mismo estilo que las de Configuración: subrayado
            #477FFA en la activa y texto #1E1E1F en todas. El historial es otra
            pantalla, así que su pestaña navega — era el botón azul que estaba
            suelto en el encabezado. */}
        <div
          className="flex flex-wrap items-end gap-3 mt-4 -mb-3 border-b"
          style={{ borderColor: '#E0E0E0' }}
        >
        <nav className="flex gap-1" aria-label="Secciones del panel">
          {([
            { id: 'activos', nombre: `Activos (${totalActivos})` },
            { id: 'descartados', nombre: `Descartados (${descartadosVisibles.length})` },
          ] as const).map((tab) => (
            <button
              key={tab.id}
              data-recorrido={tab.id === 'descartados' ? 'tab-descartados' : undefined}
              onClick={() => cambiarPestana(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${
                pestana === tab.id
                  ? 'border-[#477FFA] font-semibold'
                  : 'border-transparent hover:border-gray-200'
              }`}
              style={{ color: '#1E1E1F' }}
            >
              {tab.nombre}
            </button>
          ))}
          {userHasPermission(
            'rh.reclutamiento_candidatos.base.read',
            user?.rbac?.effectivePermissions || [],
          ) && (
            <button
              onClick={() => cambiarPestana('historial')}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm transition-colors border-b-2 -mb-px ${
                pestana === 'historial'
                  ? 'border-[#477FFA] font-semibold'
                  : 'border-transparent hover:border-gray-200'
              }`}
              style={{ color: '#1E1E1F' }}
            >
              Historial de vacantes
            </button>
          )}
        </nav>

          {/* El buscador se queda al cambiar de pestaña; solo cambia su
              texto de ayuda, porque filtra otra cosa. */}
          {/* Mismo input que los de Almacén, con el ancho del diseño. */}
          <div className="relative w-full max-w-[310px] mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={busqueda}
              onChange={(e) => {
                setBusqueda(e.target.value)
                setPagina(1)
              }}
              placeholder={
                pestana === 'activos'
                  ? 'Buscar en Panel de candidatos...'
                  : pestana === 'descartados'
                    ? 'Buscar en Descartados...'
                    : 'Buscar en Historial de vacantes...'
              }
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998] focus:border-transparent"
            />
          </div>

          {pestana === 'descartados' && seleccionados.length > 0 && (
            <button
              data-recorrido="vaciar"
              onClick={() => setPorEliminar(seleccionados)}
              className={estiloDeBoton('peligro', 'flex-shrink-0 ml-auto mb-2')}
            >
              <Trash2 className="w-4 h-4" />
              Vaciar descartados ({seleccionados.length})
            </button>
          )}

          <button
            onClick={loadAllData}
            className={estiloDeBoton(
              'primario',
              `flex-shrink-0 mb-2 ${
                pestana === 'descartados' && seleccionados.length > 0 ? '' : 'ml-auto'
              }`,
            )}
          >
            <RefreshCw className="w-4 h-4" />
            <span>Actualizar</span>
          </button>

          {puedeDarDeAlta && (
            <button
              onClick={() => navigate('/rh/candidatos/nuevo')}
              className={estiloDeBoton('primario', 'flex-shrink-0 mb-2')}
            >
              <UserPlus className="w-4 h-4" />
              Nuevo candidato
            </button>
          )}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <XCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {pestana === 'activos' ? (
        <div className="px-2 sm:px-3 pt-3 pb-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 h-[calc(100vh-290px)] min-h-[420px]">
            {COLUMNAS.map((columna) => (
              <CandidateColumn
                key={columna.clave}
                title={columna.titulo}
                color={columna.color}
                accionDeEntrada={columna.accionDeEntrada}
                candidates={candidatosDeColumna(columna.clave)}
                count={candidatosDeColumna(columna.clave).length}
                onCandidateClick={handleCandidateClick}
                onActionClick={handleActionClick}
                processingCandidateId={procesandoId}
                arrastrando={arrastrando}
                onArrastrarInicio={setArrastrando}
                onArrastrarFin={() => setArrastrando(null)}
                onSoltar={handleSoltar}
                aceptaSoltar={aceptaSoltar}
              />
            ))}
          </div>
        </div>
      ) : pestana === 'historial' ? (
        /* El historial vive aquí mismo en vez de mandar a otra ruta: es una
           pestaña más del panel, no otra pantalla. */
        <div className="px-2 sm:px-3 pt-3 pb-10">
          <JobRequestsHistory embedded busqueda={busqueda} />
        </div>
      ) : (
        /* Descartados: listado en tabla, con el mismo estilo que las demás
           tablas del sistema. */
        <div className="px-2 sm:px-3 pt-3 pb-10">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr style={{ backgroundColor: '#FAF9FF', borderBottom: '1px solid #E4E4E4' }}>
                    <th className="w-12 py-3 pl-6">
                      <Checkbox
                        data-recorrido="seleccionar-todos"
                        aria-label="Seleccionar todos"
                        checked={todosSeleccionados}
                        onChange={(e) =>
                          setSeleccionados(
                            e.target.checked ? descartadosPagina.map((c) => c.id) : [],
                          )
                        }
                      />
                    </th>
                    {[
                      'Candidato',
                      'Contacto',
                      'Etapa al descartar',
                      'Motivo',
                      'Comentario',
                      'Descartado por',
                      'Fecha de descarte',
                      'Acciones',
                    ].map((encabezado) => (
                      <th
                        key={encabezado}
                        className="text-left py-3 px-3 text-sm font-semibold whitespace-nowrap"
                        style={{ color: '#534D59' }}
                      >
                        {encabezado}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {descartadosPagina.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-16 text-center text-gray-400">
                        <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">
                          {busqueda
                            ? 'Ningún descartado coincide con la búsqueda'
                            : 'No hay candidatos descartados'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    descartadosPagina.map((candidate) => (
                      <tr
                        key={candidate.id}
                        onClick={() => handleCandidateClick(candidate.id)}
                        className={`hover:bg-[#F4F7FB] transition-colors cursor-pointer ${claseDeFila(candidate.id)}`}
                        style={{ borderBottom: '1px solid #E4E4E4' }}
                      >
                        <td className="w-12 py-3 pl-6" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            aria-label={`Seleccionar a ${candidate.fullName}`}
                            checked={seleccionados.includes(candidate.id)}
                            onChange={(e) =>
                              setSeleccionados((previos) =>
                                e.target.checked
                                  ? [...previos, candidate.id]
                                  : previos.filter((id) => id !== candidate.id),
                              )
                            }
                          />
                        </td>
                        <td className="py-3 px-3 text-gray-900 whitespace-nowrap">
                          {candidate.fullName}
                        </td>
                        <td className="py-3 px-3 text-gray-700 whitespace-nowrap">
                          {candidate.email}
                        </td>
                        <td className="py-3 px-3 text-gray-700 whitespace-nowrap">
                          {etiquetaDeEtapa(candidate.descarteEtapa)}
                        </td>
                        <td className="py-3 px-3 text-gray-700 whitespace-nowrap">
                          {candidate.descarteMotivo || <span className="text-gray-400">------</span>}
                        </td>
                        <td
                          className="py-3 px-3 whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {candidate.descarteComentario ? (
                            <button
                              onClick={() => setVerComentario(candidate)}
                              className="hover:opacity-80 transition-opacity"
                              style={{
                                fontWeight: 600,
                                fontSize: '14px',
                                lineHeight: '100%',
                                color: '#315FB9',
                              }}
                            >
                              Ver comentario
                            </button>
                          ) : (
                            <span className="text-gray-400">------</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-gray-700 whitespace-nowrap">
                          {candidate.descartadoPor ? (
                            nombreDeQuienDescarto(candidate.descartadoPor)
                          ) : (
                            <span className="text-gray-400">------</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-gray-700 whitespace-nowrap">
                          {candidate.descartadoEn ? (
                            fechaDeDescarte(candidate.descartadoEn)
                          ) : (
                            <span className="text-gray-400">------</span>
                          )}
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-3">
                            <button
                              data-recorrido="devolver-fila"
                              onClick={() => abrirDevolver(candidate)}
                              title="Devolver al tablero"
                              className="hover:opacity-70 transition-opacity"
                              style={{ color: '#315FB9' }}
                            >
                              <ArrowLeft className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setPorEliminar([candidate.id])}
                              title="Eliminar definitivamente"
                              className="text-[#EF4444] hover:opacity-70 transition-opacity"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="flex items-center justify-center gap-1 py-4 border-t" style={{ borderColor: '#E4E4E4' }}>
                <button
                  onClick={() => setPagina((p) => Math.max(1, p - 1))}
                  disabled={pagina === 1}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => setPagina(n)}
                    className={`w-8 h-8 text-sm rounded-lg border transition-colors ${
                      n === pagina
                        ? 'text-white border-transparent'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                    style={n === pagina ? { backgroundColor: '#253579' } : undefined}
                  >
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                  disabled={pagina === totalPaginas}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {modalesDeAcciones}

      {/* Devolver al tablero: elige vacante —la suya pudo cubrirse— y columna. */}
      {porDevolver && (
        <CapaDeModal zIndex={60}>
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-xl p-6">
            <button
              onClick={() => setPorDevolver(null)}
              className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>

            <h2
              className="pr-8"
              style={{
                color: '#1E1E1F',
                fontWeight: 600,
                fontSize: '22px',
                lineHeight: '28px',
              }}
            >
              Devolver al tablero de candidatos
            </h2>
            <p
              className="mt-1"
              style={{
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '18px',
                color: '#686869',
              }}
            >
              Vuelve a la etapa que le corresponde según lo que ya tiene: no se reinicia su proceso ni se repiten sus entrevistas.
            </p>

            <div className="mt-5 rounded-xl p-4" style={{ border: '1px solid #E0E0E0' }}>
              <div className="flex items-center gap-2">
                <User
                  className="flex-shrink-0"
                  style={{ width: '16px', height: '16px', color: '#1E1E1F' }}
                />
                <span
                  style={{
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '28px',
                    color: '#1E1E1F',
                  }}
                >
                  {porDevolver.fullName}
                  {porDevolver.jobRequest?.puestoSolicitar
                    ? `- ${porDevolver.jobRequest.puestoSolicitar}`
                    : ''}
                </span>
              </div>
              <p
                className="pl-6"
                style={{
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '28px',
                  color: '#686869',
                }}
              >
                Descartado en {etiquetaDeEtapa(porDevolver.descarteEtapa)}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vacante <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <SelectBuscable
                  variante="formulario"
                  value={vacanteDestino}
                  onChange={elegirVacanteDeRegreso}
                  placeholder="Selecciona la vacante"
                  placeholderDeBusqueda="Buscar vacante..."
                  opciones={vacantesParaRegresar(vacantes).map((vacante) => ({
                    value: String(vacante.id),
                    label: etiquetaDeVacante(vacante),
                  }))}
                />
              </div>
            </div>

            {/* La etapa no se elige: la decide su historial. */}
            {vacanteDestino && (
              <div
                className="mt-3 rounded-xl p-4 text-sm"
                style={{ backgroundColor: '#F6F9FB', border: '1px solid #E0E0E0' }}
                aria-live="polite"
              >
                {consultandoEtapa ? (
                  <span className="inline-flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Revisando su historial...
                  </span>
                ) : errorDeEtapa ? (
                  <span className="text-[#EF4444]">{errorDeEtapa}</span>
                ) : etapaDeRegreso ? (
                  <>
                    <p className="text-gray-700">
                      Volverá a{' '}
                      <span className="font-semibold text-[#1E1E1F]">
                        {nombreDeLaEtapaDeRegreso(etapaDeRegreso.stage)}
                      </span>
                    </p>
                    <p className="mt-0.5 text-xs text-[#686869]">{etapaDeRegreso.reason}</p>
                  </>
                ) : null}
              </div>
            )}

            {/* Sin vacantes activas no hay a dónde devolverlo, y el select vacío
                no lo explica solo. */}
            {vacantesParaRegresar(vacantes).length === 0 && (
              <p className="text-xs text-[#EF4444] mt-2">
                No hay vacantes con plazas por cubrir: primero se necesita una vacante
                abierta para devolver al candidato.
              </p>
            )}

            <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
              Comentario del regreso
            </label>
            <textarea
              value={motivoReactivacion}
              onChange={(e) => setMotivoReactivacion(e.target.value)}
              rows={3}
              placeholder="Agrega un comentario..."
              className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-[#477FFA] resize-none"
            />

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setPorDevolver(null)}
                disabled={devolviendo}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarDevolucion}
                disabled={!vacanteDestino || !etapaDeRegreso || consultandoEtapa || devolviendo}
                className={estiloDeBoton('primario')}
              >
                {devolviendo ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Comentario del descarte. Es de solo lectura: muestra de qué etapa
          salió el candidato, quién lo descartó y cuándo. */}
      {verComentario && (
        <CapaDeModal zIndex={60}>
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-xl p-6">
            <button
              onClick={() => setVerComentario(null)}
              className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>

            <h2
              className="pr-8"
              style={{
                color: '#1E1E1F',
                fontWeight: 600,
                fontSize: '22px',
                lineHeight: '28px',
              }}
            >
              Ver comentarios de candidato descartado
            </h2>

            <div
              className="mt-5 rounded-xl p-4"
              style={{ border: '1px solid #E0E0E0' }}
            >
              <div className="flex items-center gap-2">
                <User
                  className="flex-shrink-0"
                  style={{ width: '16px', height: '16px', color: '#1E1E1F' }}
                />
                <span
                  style={{
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '28px',
                    color: '#1E1E1F',
                  }}
                >
                  {verComentario.fullName}
                  {verComentario.jobRequest?.puestoSolicitar
                    ? `- ${verComentario.jobRequest.puestoSolicitar}`
                    : ''}
                </span>
              </div>
              <p
                className="pl-6"
                style={{
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '28px',
                  color: '#686869',
                }}
              >
                Descartado en {etiquetaDeEtapa(verComentario.descarteEtapa)}
              </p>
            </div>

            <div
              className="mt-3 rounded-xl p-4"
              style={{ border: '1px solid #E0E0E0' }}
            >
              <div
                className="flex items-center gap-5"
                style={{
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: '#686869',
                }}
              >
                <span>Por {nombreDeQuienDescarto(verComentario.descartadoPor)}</span>
                <span className="flex items-center gap-1.5">
                  <Calendar style={{ width: '16px', height: '16px' }} />
                  {fechaHoraDeDescarte(verComentario.descartadoEn)}
                </span>
              </div>
              <p
                className="mt-3 whitespace-pre-line"
                style={{
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '24px',
                  color: '#1E1E1F',
                }}
              >
                {verComentario.descarteComentario}
              </p>
            </div>

            {/* Modal de solo vista: la única acción es cerrarlo. */}
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setVerComentario(null)}
                className={estiloDeBoton('secundario')}
              >
                Cerrar
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Confirmación de borrado. Es definitivo y no hay deshacer, así que se
          dice cuántos son y se nombra al candidato cuando es uno solo. */}
      {porEliminar && (
        <CapaDeModal zIndex={60}>
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#FEE2E2' }}
              >
                <Trash2 className="w-5 h-5" style={{ color: '#EF4444' }} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {porEliminar.length === 1
                    ? 'Eliminar candidato descartado'
                    : `Eliminar ${porEliminar.length} candidatos descartados`}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {porEliminar.length === 1
                    ? `Se eliminará a ${
                        rejectedCandidates.find((c) => c.id === porEliminar[0])?.fullName ??
                        'este candidato'
                      } de forma definitiva.`
                    : 'Se eliminarán de forma definitiva.'}{' '}
                  Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setPorEliminar(null)}
                disabled={eliminando}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              <button
                onClick={() => eliminarDescartados(porEliminar)}
                disabled={eliminando}
                className={estiloDeBoton('peligro')}
              >
                {eliminando && <Loader2 className="w-4 h-4 girando" />}
                {eliminando ? 'Eliminando…' : 'Eliminar'}
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Modal de Agendar Entrevista */}
      {showInterviewModal && (
        <CapaDeModal>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Agendar Entrevista RH</h3>
              <button
                onClick={() => setShowInterviewModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitInterview} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de Entrevista *
                  </label>
                  <input
                    type="date"
                    value={interviewForm.fechaEntrevista}
                    onChange={(e) => handleInterviewFormChange('fechaEntrevista', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      interviewErrors.fechaEntrevista ? 'border-red-300' : 'border-gray-300'
                    }`}
                    required
                  />
                  {interviewErrors.fechaEntrevista && (
                    <p className="mt-1 text-xs text-red-600">{interviewErrors.fechaEntrevista}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora *
                  </label>
                  <input
                    type="time"
                    value={interviewForm.horaEntrevista}
                    onChange={(e) => handleInterviewFormChange('horaEntrevista', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      interviewErrors.horaEntrevista ? 'border-red-300' : 'border-gray-300'
                    }`}
                    required
                  />
                  {interviewErrors.horaEntrevista && (
                    <p className="mt-1 text-xs text-red-600">{interviewErrors.horaEntrevista}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de entrevista *
                </label>
                <SelectBuscable
                  variante="formulario"
                  value={interviewForm.modalidad}
                  onChange={(valor) => handleInterviewFormChange('modalidad', valor)}
                  required
                  canDelete={false}
                  placeholderDeBusqueda="Buscar tipo..."
                  opciones={[
                    { value: 'presencial', label: 'Presencial' },
                    { value: 'virtual', label: 'Virtual' },
                  ]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {interviewForm.modalidad === 'virtual' ? 'Link de la Reunión *' : 'Ubicación *'}
                </label>
                <input
                  type="text"
                  value={interviewForm.ubicacionLink}
                  onChange={(e) => handleInterviewFormChange('ubicacionLink', e.target.value)}
                  placeholder={interviewForm.modalidad === 'virtual' ? 'https://zoom.us/j/...' : 'Oficina central, Sala 1'}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    interviewErrors.ubicacionLink ? 'border-red-300' : 'border-gray-300'
                  }`}
                  required
                />
                {interviewErrors.ubicacionLink && (
                  <p className="mt-1 text-xs text-red-600">{interviewErrors.ubicacionLink}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entrevistador *
                </label>
                <SelectBuscable
                  variante="formulario"
                  value={interviewForm.entrevistadorId}
                  onChange={(valor) => handleInterviewFormChange('entrevistadorId', valor)}
                  error={Boolean(interviewErrors.entrevistadorId)}
                  required
                  placeholder="Seleccionar entrevistador"
                  placeholderDeBusqueda="Buscar entrevistador..."
                  opciones={availableInterviewers.map((interviewer) => ({
                    value: String(interviewer.id),
                    label: `${interviewer.firstName} ${interviewer.lastName}${interviewer.role ? ` (${interviewer.role})` : ''}`,
                  }))}
                />
                {interviewErrors.entrevistadorId && (
                  <p className="mt-1 text-xs text-red-600">{interviewErrors.entrevistadorId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observaciones
                </label>
                <textarea
                  value={interviewForm.observaciones}
                  onChange={(e) => handleInterviewFormChange('observaciones', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Observaciones adicionales..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInterviewModal(false)}
                  className={estiloDeBoton('secundario', 'flex-1')}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingInterview}
                  className={estiloDeBoton('primario', 'flex-1')}
                >
                  {isSubmittingInterview ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Programando...
                    </>
                  ) : (
                    <>
                      <Calendar className="w-4 h-4" />
                      Programar Entrevista
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </CapaDeModal>
      )}

      {/* Recorrido guiado de la bandeja de descartados */}
      {recorridoAbierto && (
        <Recorrido pasos={pasosRecorrido} onCerrar={cerrarRecorrido} />
      )}

      {/* Notification Component */}
      <Notification
        type={notification.type}
        title={notification.title}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </div>
  )
}
